import React, { useEffect, useState, useRef, useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from '@emotion/react';
import { darkTheme, lightTheme } from '../themes';
import { useTranslation } from 'react-i18next';
import { formatNumber } from '@/utils/formatters';
import useResponsiveChartHeight from '@/components/highcharts/powerAnalyser/hooks/useResponsiveChartHeight';

const getYear = (date) => {
  return new Date(date).getFullYear();
};

const formatTimestamp = (timestamp, aggregate) => {
  const date = new Date(timestamp);

  switch (aggregate) {
    case 'hour':
      return date.toLocaleString('da-DK', {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false, // Use 24-hour format
      });

    case 'day':
      return date.toLocaleDateString('da-DK', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });

    case 'week': {
      const weekNumber = getWeekNumber(date);
      const year = getYear(date);
      return `uge ${weekNumber} ${year}`;
    }

    case 'month':
      return date.toLocaleDateString('da-DK', {
        month: 'short',
      });

    case 'year':
      return date.toLocaleDateString('da-DK', {
        year: 'numeric',
      });

    default:
      return date.toLocaleString('da-DK');
  }
};

const getWeekNumber = (date) => {
  const target = new Date(date.valueOf());
  const dayNr = (date.getDay() + 6) % 7; // Make Monday=0
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  const firstWeekDay = (target.getDay() + 6) % 7;
  target.setDate(target.getDate() - firstWeekDay + 3);
  const firstThursdayOfYear = target.valueOf();

  return (
    1 +
    Math.round((firstThursday - firstThursdayOfYear) / (7 * 24 * 3600 * 1000))
  );
};

const EnergyAnalysisSingleSeriesChart = ({
  // nodeData,
  lazyLoadedEnergyConsumptionData,
  isLoading,
  viewMode,
  aggregate,
  dataSeriesAggregate,
  selectedEnergyGroup,
  setSeriesColors,
}) => {
  const chartComponentRef = useRef(null);
  const [chartOptions, setChartOptions] = useState({});
  const theme = useTheme();
  const { t } = useTranslation('energyxplorer-analysis-tabs');
  const chartHeight = useResponsiveChartHeight();

  const getFilterLabel = (type, value) => {
    switch (type) {
      case 'activeHoursFilter':
        switch (value) {
          case true:
            return t('filter_labels.active_hours');
          case false:
            return t('filter_labels.inactive_hours');
          default:
            return t('filter_labels.all_hours');
        }
      case 'activeDaysFilter':
        switch (value) {
          case true:
            return t('filter_labels.active_days');
          case false:
            return t('filter_labels.inactive_days');
          default:
            return t('filter_labels.all_days');
        }
      case 'activeHolidaysFilter':
        switch (value) {
          case true:
            return t('filter_labels.holidays_included');
          case false:
            return t('filter_labels.holidays_excluded');
          default:
            return '';
        }
      default:
        return '';
    }
  };

  const generateLegendName = (series) => {
    if (!series || !series.metadata) return '';
    const { activeDaysFilter, activeHoursFilter, activeHolidaysFilter } =
      series.metadata || {};

    const parts = [];

    parts.push(getFilterLabel('activeDaysFilter', activeDaysFilter));

    parts.push(getFilterLabel('activeHoursFilter', activeHoursFilter));

    const holidayLabel = getFilterLabel(
      'activeHolidaysFilter',
      activeHolidaysFilter,
    );
    if (holidayLabel) parts.push(holidayLabel);

    return parts.length > 0 ? parts.join(', ') : '';
  };

  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) {
        chart.showLoading('Loading data...');
      } else {
        chart.hideLoading();
      }
    }
  }, [isLoading]);

  useEffect(() => {
    const chart = chartComponentRef.current?.chart;
    if (chart) {
      if (isLoading) chart.showLoading('Loading data...');
      else chart.hideLoading();
    }

    if (
      !lazyLoadedEnergyConsumptionData ||
      lazyLoadedEnergyConsumptionData[0] === null
    )
      return;

    let chartSeries = [];
    let uniqueTimestamps = [];

    uniqueTimestamps = [
      ...new Set(
        lazyLoadedEnergyConsumptionData.flatMap((series) =>
          series?.EnergyData?.map((item) => new Date(item.timestamp).getTime()),
        ),
      ),
    ].sort((a, b) => a - b);

    chartSeries = lazyLoadedEnergyConsumptionData.map((series, index) => ({
      name: generateLegendName(series),
      data: uniqueTimestamps.map((timestamp) => {
        const dataPoint = series?.EnergyData?.find(
          (d) => new Date(d.timestamp).getTime() === timestamp,
        );

        const value = dataPoint?.[dataSeriesAggregate.value]; // dynamically access sum, mean, min, max

        return [timestamp, value !== undefined ? value : null];
      }),
      type: viewMode === 'line' ? 'line' : 'column',
    }));

    // ----- Final Chart Options -----
    setChartOptions({
      chart: {
        type: viewMode === 'line' ? 'line' : 'column',
        height: chartHeight,
        zooming: { type: 'x', mouseWheel: { enabled: false } },
        spacingLeft: 40, // ✅ Adds left padding
        spacingRight: 40, // ✅ Adds right padding
      },
      time: {
        // timezoneOffset: new Date().getTimezoneOffset('da-DK'),
        useUTC: false,
      },
      credits: {
        enabled: false,
      },
      title: { text: '' },
      xAxis: {
        type: 'datetime',
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
        // plotBands,
        labels: {
          formatter: function () {
            return formatTimestamp(this.value, aggregate);
          },
          style: {
            whiteSpace: 'normal',
            width: '30px',
            textAlign: 'center',
          },
        },
      },
      yAxis: {
        min: 0,
        title: { text: 'Energiforbrug (kWh)' },
        crosshair: { width: 1, color: 'gray', dashStyle: 'shortdot' },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        formatter: function () {
          let tooltip = ``;
          this.points.forEach((point) => {
            tooltip += `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${
              point.color
            };margin-right:5px;"></span>${point.series.name}: <b>${formatNumber(
              point.y,
            )} kWh</b>
            <span style="font-size:12px;color:gray;">(${formatTimestamp(
              point.x,
              aggregate,
            )})</span><br/>`;
          });
          return tooltip;
        },
      },
      plotOptions: {
        line: { marker: { enabled: false } }, // Smooth line
        column: { grouping: true, borderColor: 'transparent' }, // Properly grouped columns
      },
      series: chartSeries,
    });
  }, [
    // nodeData,
    lazyLoadedEnergyConsumptionData,
    viewMode,
    aggregate,
    dataSeriesAggregate,
    isLoading,
    selectedEnergyGroup,
  ]);

  useEffect(() => {
    if (!setSeriesColors || typeof setSeriesColors !== 'function') return;

    const chart = chartComponentRef.current?.chart;
    if (!chart) return;

    const colorMap = chart.series.map((s, i) => ({
      name: s.name,
    }));

    setSeriesColors(colorMap);
  }, [chartOptions, setSeriesColors]);

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartComponentRef.current?.chart) {
      const chart = chartComponentRef.current.chart;
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          time: {
            // timezoneOffset: new Date().getTimezoneOffset('da-DK'),
            useUTC: true,
          },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          },
          yAxis: [
            {
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          // tooltip: {
          //   backgroundColor: themeOptions.tooltip.backgroundColor,
          //   borderColor: themeOptions.tooltip.borderColor,
          //   style: {
          //     color: theme.palette.mode === 'dark' ? 'white' : 'black', // ✅ Dynamic text color
          //   },
          // },
          tooltip: {
            shared: true,
            useHTML: true,
            formatter: function () {
              let tooltip = ``;

              this.points.forEach((point) => {
                const actualTime = formatTimestamp(
                  point.point.timestamp,
                  aggregate,
                );
                tooltip += `
                <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${
                  point.series.color
                };margin-right:5px;"></span>
                ${point.series.name}: <b>${formatNumber(point.y)} kWh</b>
                <span style="font-size:12px;color:gray;">(${actualTime})</span><br/>
              `;
              });

              return tooltip;
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);

  return (
    <>
      <HighchartsReact
        highcharts={Highcharts}
        options={chartOptions}
        ref={chartComponentRef}
      />
    </>
  );
};

export default EnergyAnalysisSingleSeriesChart;
